import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import type { CountriesAlpha2, State } from '@sequencehq/api/commonEnums'
import { useQuery } from '@sequencehq/api/utils'
import { type ReactNode, useMemo } from 'react'

import { AvalaraBlocker } from 'Integrations/index'
import { useTaxIntegrationProvider } from 'lib/hooks/useTaxIntegrationProvider.ts'

import { ConfirmationBlocker } from './ConfirmationBlocker'
import {
  type TaxValidationTaxCategory,
  useTaxRateValidation
} from './useTaxRateValidation'

type Props = {
  entityName: 'invoice' | 'credit note'
  onClose: () => void
  confirmTitleText: string
  blockedTitle: string
  customerTaxStatus?: 'TAXED' | 'TAX_EXEMPT' | 'REVERSE_CHARGED'
  customerCountry?: CountriesAlpha2
  customerState?: State
  taxCategories: TaxValidationTaxCategory[]
  recalculate: () => void | Promise<void>
}

export const useTaxValidation = ({
  entityName,
  onClose,
  confirmTitleText,
  blockedTitle,
  customerTaxStatus,
  customerCountry,
  customerState,
  taxCategories,
  recalculate
}: Props): {
  loading: boolean
  blocker: ReactNode | null
  validation: ReturnType<typeof useTaxRateValidation>['validation']
} => {
  const { isAvalara, isLoading: taxIntegrationLoading } =
    useTaxIntegrationProvider()
  const avalaraMappingsQuery = useQuery(
    dashboardv99990101Client.getAllTaxCategoriesWithAvalaraMappings,
    undefined,
    { enabled: isAvalara }
  )
  const {
    isLoading: taxRatesLoading,
    isTaxRatesCheckFailed,
    validation
  } = useTaxRateValidation({
    onClose,
    customerTaxStatus,
    customerCountry,
    customerState,
    taxCategories,
    recalculate
  })

  const hasUnmappedAvalaraTaxCategory = useMemo(() => {
    if (!isAvalara) {
      return false
    }

    const mappings = avalaraMappingsQuery.data?.items ?? []

    return taxCategories.some(taxCategory => {
      const mapping = mappings.find(
        item => item.taxCategoryId === taxCategory.id
      )

      return !mapping?.avalaraTaxCode
    })
  }, [avalaraMappingsQuery.data?.items, isAvalara, taxCategories])

  const loading =
    taxIntegrationLoading ||
    taxRatesLoading ||
    (isAvalara && avalaraMappingsQuery.isLoading)

  const blocker = (() => {
    if (hasUnmappedAvalaraTaxCategory) {
      return (
        <AvalaraBlocker
          isOpen
          confirmTitleText={confirmTitleText}
          onClose={onClose}
        >
          This {entityName} includes line items in a tax category that has not
          yet been mapped to an Avalara tax code.
        </AvalaraBlocker>
      )
    }

    // Fail closed: without every tax rate we can't tell whether any are
    // missing, so block rather than send with tax we couldn't check.
    if (isTaxRatesCheckFailed) {
      return (
        <ConfirmationBlocker
          title={blockedTitle}
          onClose={onClose}
          data-testid="common.taxRatesBlocker"
        >
          We couldn't load your tax rates to check this {entityName}. Please try
          again.
        </ConfirmationBlocker>
      )
    }

    return null
  })()

  return { loading, blocker, validation }
}
